import { WATERFALL_VARIANT_MAX_INITIAL_RESULTS } from 'app/ProviderSearch/constants'

export type BudgetSplit<T> = {
  rendered: T[]
  buffered: T[]
}

/**
 * Splits a freshly fetched batch into the part that may render now and the part
 * held back for the next reveal.
 *
 * A `null` budget means unbounded, which is control's behaviour: everything
 * renders and nothing is buffered.
 */
export const splitByBudget = <T>(
  items: T[],
  budget: number | null
): BudgetSplit<T> => {
  if (budget === null) {
    return { rendered: items, buffered: [] }
  }

  const safeBudget = Math.max(0, budget)

  return {
    rendered: items.slice(0, safeBudget),
    buffered: items.slice(safeBudget),
  }
}

/**
 * Partial matches the variant may auto-fill into the initial view, given how many
 * exact matches already render. Six exact matches or more leaves no room, so the
 * list stays exact-only until the user asks for more.
 */
export const getInitialPartialBudget = (directMatchCount: number): number =>
  Math.max(0, WATERFALL_VARIANT_MAX_INITIAL_RESULTS - directMatchCount)

/**
 * A secondary page that contained only providers without a practice is not the
 * end of the bucket. Keep going while the API count extends past that page.
 * An empty page, or a page that still had usable providers (duplicates), stays
 * exhausted — that is the existing stop.
 */
export const shouldContinuePastRejectedPage = (
  returnedCount: number,
  usableCount: number,
  paginationCount: number,
  fetchedOffset: number
): boolean =>
  returnedCount > 0 &&
  usableCount === 0 &&
  paginationCount > fetchedOffset + returnedCount

/**
 * Total available after the relaxation that is currently in effect.
 *
 * A new bucket, or show-more on the latest bucket, adopts the API count for
 * that query. Show-more on an older bucket keeps the broader total. A missing
 * or zero count (the API returns 0 when the page itself is empty) must not
 * wipe a positive total.
 */
export const nextRelaxedProvidersTotal = (
  previous: number | undefined,
  responseCount: number | undefined,
  isCurrentRelaxation: boolean
): number | undefined => {
  if (!responseCount || responseCount <= 0) {
    return previous
  }

  if (!isCurrentRelaxation) {
    return previous
  }

  return responseCount
}

/**
 * Show-more may refresh the relaxed total only when the bucket being paged is
 * the relaxation that owns that total.
 *
 * A newer relaxation can return only providers already on screen and never
 * become a bucket. Paging the previous bucket must not replace the newer total.
 */
export const showMoreOwnsRelaxedTotal = (
  bucketRelaxationId: number | undefined,
  totalRelaxationId: number | undefined
): boolean =>
  bucketRelaxationId !== undefined && bucketRelaxationId === totalRelaxationId
